/**
 * Activités système — la seule trace persistante du Mode Debug.
 *
 * Activation, désactivation, expiration, purge : qui, quand, sur quelle fiche.
 * Aucun contenu technique (le serveur n'en enregistre pas).
 */
import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import api from "@/lib/crmApi";
import { Empty } from "@/crm/ui";

const TONE = {
  MODE_DEBUG_ENABLED: "bg-indigo-50 text-indigo-700",
  MODE_DEBUG_DISABLED: "bg-slate-100 text-slate-600",
  MODE_DEBUG_EXPIRED: "bg-amber-50 text-amber-700",
  COMPANY_DATA_PURGED: "bg-rose-50 text-rose-700",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function SystemActivities({ companyId, limit = 50, compact = false, refreshKey = 0 }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    const params = { limit };
    if (companyId) params.company_id = companyId;
    api.get("/admin/system-activities", { params })
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [companyId, limit, refreshKey]);

  if (!items) return null;
  if (items.length === 0) {
    return compact ? <p className="text-xs text-slate-400" data-testid="system-activities-empty">Aucune action administrative.</p>
      : <Empty label="Aucune action administrative" icon={ShieldAlert} />;
  }
  return (
    <ul className="space-y-2" data-testid="system-activities">
      {items.map((a) => {
        const counts = a.details?.deleted_labelled || {};
        const lignes = Object.entries(counts);
        return (
          <li key={a.id} className="flex gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5">
            <div className="text-[11px] font-mono text-slate-400 whitespace-nowrap pt-0.5">{fmt(a.created_at)}</div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-md ${TONE[a.type] || "bg-slate-100 text-slate-600"}`}>{a.label}</span>
                <span className="text-xs text-slate-500">Par : {a.admin_name}</span>
                {a.company_name && !companyId && <span className="text-xs text-slate-500">· {a.company_name}</span>}
                {a.details?.duration_minutes && a.type === "MODE_DEBUG_ENABLED" && (
                  <span className="text-xs text-slate-500">· {a.details.duration_minutes} min</span>
                )}
              </div>
              {lignes.length > 0 && (
                <div className="mt-1 text-xs text-slate-600">
                  Données supprimées : {lignes.map(([label, n]) => `${n} ${label}`).join(", ")}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
